import { PrismBanner, PrismButton, PrismDialog } from "@prism-ui/react"
import { EmailReminder, Option } from "./types"
import './EmailReminderItem.css'
import { useState } from "react"
import { DateTime } from "luxon"
import RelativeTime from "../../../RelativeTime"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/free-solid-svg-icons"

export const timeAgo = (dateTime: DateTime) => {
  const units:Intl.RelativeTimeFormatUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second']
  const diff = dateTime.diffNow().shiftTo(...units)
  const unit = units.find((unit) => diff.get(unit) !== 0) || 'second'
  const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto', })
  return relativeFormatter.format(Math.trunc(diff.as(unit)), unit)
}

interface UrlRecord {
  id: number
  name: string
  url: string
}

const frequencyDisplayLabels: Record<string, string> = {
  daily: 'Daily',
  business_daily: 'Business Days',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

interface Props {
  emailReminder: EmailReminder
  onClick: (id:number, er:EmailReminder)=>void
  onDelete: (id:number, er:EmailReminder)=>void
  onSendNow?: (id:number)=>void
  recipientOptions: Option[]
  chapterOptions: Option[]
  urlOptions?: UrlRecord[]
}
const EmailReminderItem = ({emailReminder:er, onClick, onDelete, onSendNow, recipientOptions, chapterOptions, urlOptions = []}:Props) => {
  const findLabel = (s:string, o:Option[]) => {
    return (o.find(x=>x.value == s) || {label:''}).label
  }
  const fmtList = (s:string, o:Option[]) => {
    return s.split(',')
    .map(x=>x.trim())
    .map(x=>findLabel(x, o))
    .filter(x=>!!x)
    .join(', ')
  }
  const fmtBodyText = (html:string) => {
    const el = document.createElement('div')
    el.innerHTML = html
    return Array.from(el.children).map(x=>(x as HTMLElement).innerText).join(' ')
  }

  const [deleting, setDeleting] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [showAllRecipients, setShowAllRecipients] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!onDelete) return
    try {
      setDeleting(true)
      await onDelete(er.id, er)
      setShowDeleteModal(false)
    } finally {
      setDeleting(false)
    }
  }

  const handleSendNow = async () => {
    if (!onSendNow) return
    setSendError(null)

    // Validate body is non-empty before submitting
    const bodyText = (er.htmlBody ?? '').replace(/<[^>]*>/g, '').trim()
    if (!bodyText) {
      setSendError('Email body cannot be empty.')
      return
    }

    try {
      setSending(true)
      await onSendNow(er.id)
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send email.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <div className={`email-reminder-row${(!er.sentOn && !er.sentTo) ? ' email-reminder-row--deletable' : ''}`}>
        <div
          className='email-reminder-item'
          role="button"
          tabIndex={0}
          onClick={()=>onClick(er.id, er)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick(er.id, er)
            }
          }}
        >
    <div className="email-reminder-grid">
      <div>
        To: <span className='to'>{fmtList(er.recipientTypes, recipientOptions)}</span>
      </div>
      <div>
        Chapter{er.chapterIds.length != 1 ? 's':''}: <span className='chapters'>{fmtList(er.chapterIds, chapterOptions)}</span>
      </div>
      <div className="email-reminder-when">
        {er.isRecurring ? (
          <span>
            <small className="email-reminder-recurring">Recurring ({frequencyDisplayLabels[er.frequency || 'business_daily']}): </small>
            {DateTime.fromISO(er.sendOn).toLocaleString(DateTime.DATE_SHORT)} - {er.endDate ? DateTime.fromISO(er.endDate).toLocaleString(DateTime.DATE_SHORT) : '?'}
          </span>
        ) : (
          <RelativeTime value={er.sentOn || er.sendOn}/>
        )}
      </div>
    </div>
    <div className="email-reminder-grid">
      <div>
        Subject: <span className='subject'>{er.subject}</span>
      </div>
      {er.includedUrlId && (
        <div>
          URL: <span className='url'>{urlOptions.find(u => u.id === er.includedUrlId)?.name || 'Unknown'}</span>
        </div>
      )}
      {!er.sentOn && onSendNow && (
        // Send Now sits inside the clickable row. Stopping the native click here is what
        // keeps it from also opening the edit modal — stopping the Prism custom event
        // would not, since the underlying click bubbles independently.
        <div className="email-reminder-sendnow" onClick={(e) => e.stopPropagation()}>
          <PrismButton
            size="sm"
            tone="success"
            display="outline"
            disabled={sending}
            loading={sending}
            onPrismClick={handleSendNow}
          >
            Send Now
          </PrismButton>
          {sendError && (
            <div className="email-reminder-send-error">
              {sendError}
            </div>
          )}
        </div>
      )}
    </div>
    <div>
      <span className='body'>{fmtBodyText(er.htmlBody)}</span>
    </div>
    {!er.sentTo ? undefined : (() => {
      const recipientList = er.sentTo.split(',').map(r => r.trim())
      const recipientCount = recipientList.length
      
      return <>
        <hr/>
        <div className="email-reminder-recipients-toggle" onClick={(e) => {
          e.stopPropagation()
          setShowAllRecipients(!showAllRecipients)
        }}>
          <strong>
            {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            <span className="email-reminder-recipients-hint">
              ({showAllRecipients ? 'click to collapse' : 'click to expand'})
            </span>
          </strong>
          {showAllRecipients && (
            <div className="email-reminder-recipients-list">
              <span className='recipients'>{er.sentTo}</span>
            </div>
          )}
        </div>
      </>
    })()}
        </div>
      
      {/* Delete Button - only show for pending emails (not sent) */}
      {(!er.sentOn && !er.sentTo) && (
        <PrismButton
          className="email-reminder-delete"
          tone="secondary"
          display="outline"
          disabled={deleting}
          onPrismClick={handleDeleteClick}
          aria-label="Delete email reminder"
        >
          <FontAwesomeIcon icon={faXmark} />
        </PrismButton>
      )}
    </div>

      {/* Delete Confirmation dialog */}
      <PrismDialog
        open={showDeleteModal}
        setAriaLabel="Confirm Delete"
        onPrismClose={() => setShowDeleteModal(false)}
      >
        <span slot="header">Confirm Delete</span>
        <div className="email-reminder-delete-body">
          <p>Are you sure you want to delete this email reminder?</p>
          <p className="email-reminder-delete-subject">&quot;{er.subject}&quot;</p>
          <PrismBanner tone="caution">
            This action cannot be undone. The email reminder will be permanently deleted.
          </PrismBanner>
        </div>

        <PrismButton
          slot="footer"
          tone="secondary"
          display="outline"
          onPrismClick={() => setShowDeleteModal(false)}
        >
          Cancel
        </PrismButton>
        <PrismButton
          slot="footer"
          tone="danger"
          display="fill"
          disabled={deleting}
          loading={deleting}
          onPrismClick={handleConfirmDelete}
        >
          Delete
        </PrismButton>
      </PrismDialog>
    </>
  )
}
export default EmailReminderItem